import React, { useEffect, useState } from "react";
import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const FALLBACK = "https://images.pexels.com/photos/20437638/pexels-photo-20437638.jpeg?auto=compress&cs=tinysrgb&w=1000";

const nowMYMinutes = () => {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kuala_Lumpur", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === "hour").value, 10);
  const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
  return h * 60 + m;
};
const toMin = (t) => { const [h, m] = (t || "").split(":").map(Number); return h * 60 + (m || 0); };
const isOpenNow = (s) => {
  if (!s.opening_time || !s.closing_time) return null;
  const now = nowMYMinutes(), o = toMin(s.opening_time), c = toMin(s.closing_time);
  return c > o ? now >= o && now < c : now >= o || now < c; // handle past-midnight close
};

export const Contact = () => {
  const { t } = useLang();
  const [stores, setStores] = useState([]);

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);

  const mapLink = (s) => s.map_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`;

  return (
    <div>
      <section className="bg-brand-forest text-brand-cream grain">
        <div className="mx-auto max-w-7xl px-5 py-16">
          <h1 className="font-display text-5xl font-black tracking-tighter text-brand-yellow sm:text-7xl">{t("contact_title")}</h1>
          <p className="mt-4 max-w-xl text-lg text-brand-cream/80">{t("contact_sub")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14">
        {stores.length === 0 && <p className="text-muted-foreground">Outlet info coming soon.</p>}
        <div className="grid gap-8 lg:grid-cols-2">
          {stores.map((s) => (
            <div key={s.id} data-testid={`store-${s.id}`} className="overflow-hidden rounded-3xl border-2 border-brand-ink bg-white shadow-brutal">
              <img src={s.image_path ? fileUrl(s.image_path) : FALLBACK} alt={s.name} className="aspect-[16/9] w-full object-cover" />
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-2xl font-bold">{s.name}</h3>
                  {isOpenNow(s) !== null && (
                    <span data-testid={`store-openstatus-${s.id}`} className={`inline-flex items-center gap-1 rounded-full border-2 border-brand-ink px-3 py-0.5 text-xs font-bold uppercase ${isOpenNow(s) ? "bg-brand-green" : "bg-muted text-muted-foreground"}`}>
                      <Clock className="h-3 w-3" /> {isOpenNow(s) ? t("open_now") : t("closed_now")}
                    </span>
                  )}
                </div>
                <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-greendark" /> {s.address}
                </p>
                {(s.hours_text || (s.opening_time && s.closing_time)) && (
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 text-brand-greendark" /> {s.hours_text || `${s.opening_time} – ${s.closing_time}`}
                  </p>
                )}
                {s.phone && (
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4 text-brand-greendark" /> {s.phone}
                  </p>
                )}
                {s.contacts && s.contacts.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-brand-greendark">{t("contacts")}</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {s.contacts.map((c, i) => (
                        <li key={i} className="flex justify-between border-b border-dashed border-brand-ink/20 py-1">
                          <span className="font-semibold">{c.name}</span>
                          <a href={`tel:${c.phone}`} className="text-brand-greendark">{c.phone}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <a href={mapLink(s)} target="_blank" rel="noopener noreferrer" data-testid={`store-navigate-${s.id}`} className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-green px-5 py-2.5 text-sm font-bold hover-brutal">
                  <Navigation className="h-4 w-4" /> {t("navigate")}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
